import { authHeader, sessionData } from "../helpers";
import { store } from "../store";
import { api } from "../config/api";
import { readJsonFromResponse } from "../utils/http/readJsonResponse";
import { extractApiResponseErrorMessage } from "../utils/http/apiError";
import { logout as authLogout } from "../store/authSlice";
import { clearPersistedSessionStorage } from "../store/clearPersistedSession";
import { userConstants } from "../constants/user.constants";

function noStoreFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, { ...init, cache: "no-store" });
}

export interface LoginUserPayload {
  key: string;
}

export interface UserSessionInfo {
  device_token: string;
  session_id: string;
  session_id_refreshed?: boolean;
  force_login?: boolean;
  is_active?: boolean;
}

export interface SessionInfoPayload {
  data: {
    session_info: UserSessionInfo;
  };
}

export interface CheckSessionPayload extends SessionInfoPayload {
  success: boolean;
  meta?: {
    input?: {
      username?: string;
    };
  };
}

export interface LoggedInUserDetailsPayload {
  role: string;
  user_id?: string | number;
  business?: { business_id?: string | number };
  business_link?: { business_id?: string | number };
  user_permissions?: Record<string, unknown>;
  profile_picture?: string;
}

export interface GetAllUsersPayload {
  success?: boolean;
  errors?: unknown;
  data?: {
    user_details?: LoggedInUserDetailsPayload;
  };
}

export type SessionRefreshResult = Record<string, unknown>;

export const userService = {
  login,
  logout,
  getAll,
  createSession,
  checkSessionValidity,
  refreshSessionToken,
  checkSessionValidityWithValues,
};

function login(username: string, password: string): Promise<LoginUserPayload> {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  };
  return fetch(api.LOGIN, requestOptions).then((response) =>
    handleResponse<LoginUserPayload>(response)
  );
}

function createSession(device_ip: string, device_info: string): Promise<SessionInfoPayload> {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({ device_ip, device_info }),
  };
  return noStoreFetch(api.CREATE_SESSION, requestOptions).then((response) =>
    handleResponse<SessionInfoPayload>(response)
  );
}

function getAuthCredentials(): { username: string | null; auth_token: string | null } {
  const state = store.getState();
  const username = state.auth?.user?.username ?? null;
  const auth_token = state.auth?.user?.key ?? null;
  return { username, auth_token };
}

function checkSessionValidity(): Promise<CheckSessionPayload> {
  const { device_token, session_id } = sessionData();
  const { username, auth_token } = getAuthCredentials();

  if (!device_token || !session_id) {
    return Promise.reject(new Error("Session id and Device token are required"));
  }
  if (!username || !auth_token) {
    return Promise.reject(new Error("Username and auth token are required"));
  }

  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({
      username,
      auth_token,
      device_token,
      session_id,
    }),
  };
  return noStoreFetch(api.CHECK_SESSION_VALIDITY, requestOptions).then((response) =>
    handleResponse<CheckSessionPayload>(response)
  );
}

function checkSessionValidityWithValues(
  device_token: string,
  session_id: string
): Promise<CheckSessionPayload> {
  const { username, auth_token } = getAuthCredentials();

  if (!username || !auth_token) {
    return Promise.reject(new Error("Username and auth token are required"));
  }

  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({
      username,
      auth_token,
      device_token,
      session_id,
    }),
  };
  return noStoreFetch(api.CHECK_SESSION_VALIDITY, requestOptions).then((response) =>
    handleResponse<CheckSessionPayload>(response)
  );
}

function refreshSessionToken(
  device_token: string,
  session_id: string
): Promise<SessionRefreshResult> {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({
      device_token,
      session_id,
    }),
  };
  return noStoreFetch(api.REFRESH_SESSION_TOKEN, requestOptions).then((response) =>
    handleResponse<SessionRefreshResult>(response)
  );
}

function logout(): Promise<unknown> {
  const { device_token, session_id } = sessionData();
  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({
      device_token,
      session_id,
    }),
  };
  return noStoreFetch(api.LOGOUT, requestOptions)
    .then((response) => handleResponse<unknown>(response))
    .catch(() => undefined);
}

function isLogoutEndpointResponse(response: Response): boolean {
  try {
    return response.url.includes("/users/logout");
  } catch {
    return false;
  }
}

let logoutFrom401InFlight = false;

function getAll(device_token: string, session_id: string): Promise<GetAllUsersPayload> {
  const { business_id } = sessionData();
  const requestOptions: RequestInit = {
    method: "POST",
    headers: authHeader(),
    body: JSON.stringify({
      device_token,
      session_id,
      business_id,
    }),
  };
  return noStoreFetch(api.GET_USER_DETAILS, requestOptions)
    .then((response) => handleResponse<GetAllUsersPayload>(response))
    .then((users) => {
      if (users.success === false) {
        alert(users.errors);
        void logout();
      }
      return users;
    });
}

function handleResponse<T>(response: Response): Promise<T> {
  return readJsonFromResponse(response)
    .then((data) => {
      if (!response.ok) {
        if (response.status === 401) {
          if (!isLogoutEndpointResponse(response) && !logoutFrom401InFlight) {
            logoutFrom401InFlight = true;
            void logout().finally(() => {
              logoutFrom401InFlight = false;
            });
          }
          store.dispatch(authLogout());
          store.dispatch({ type: userConstants.LOGOUT });
          clearPersistedSessionStorage();
        }
        const error = extractApiResponseErrorMessage(data, response.statusText);
        return Promise.reject(error);
      }
      return data as T;
    })
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      return Promise.reject(msg);
    });
}
